import { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';

const WARN_MS = 2 * 60 * 1000;
const PING_MS = 5 * 60 * 1000;
const EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

// Gentle "still there?" prompt 2 minutes before the backend idle-session timeout signs the user out.
export default function IdleWarning() {
  const { user, logout } = useAuth();
  const [remaining, setRemaining] = useState(null);
  const lastActive = useRef(Date.now());
  const lastPing = useRef(Date.now());
  const warning = useRef(false);
  const timeoutMs = ((user && user.session_idle_minutes) || 30) * 60 * 1000;

  useEffect(() => {
    if (!user) return undefined;
    const onActivity = () => { if (!warning.current) lastActive.current = Date.now(); };
    EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    const tick = setInterval(() => {
      const now = Date.now();
      const idle = now - lastActive.current;
      if (!warning.current && idle < timeoutMs - WARN_MS && now - lastPing.current > PING_MS) {
        lastPing.current = now;
        api.get('/auth/me').catch(() => {});
      }
      if (idle >= timeoutMs) {
        clearInterval(tick);
        logout().finally(() => window.location.replace('/login?reason=idle'));
        return;
      }
      if (idle >= timeoutMs - WARN_MS) { warning.current = true; setRemaining(Math.ceil((timeoutMs - idle) / 1000)); }
    }, 1000);
    return () => { clearInterval(tick); EVENTS.forEach(e => window.removeEventListener(e, onActivity)); };
  }, [user, timeoutMs, logout]);

  const stay = async () => {
    lastActive.current = Date.now(); lastPing.current = Date.now(); warning.current = false; setRemaining(null);
    try { await api.get('/auth/me'); } catch { /* interceptor handles SESSION_IDLE */ }
  };

  if (!user || remaining === null) return null;
  const mm = String(Math.floor(remaining / 60)).padStart(1, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  return (
    <Dialog open onOpenChange={(o) => { if (!o) stay(); }}>
      <DialogContent className="max-w-sm" data-testid="idle-warning-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Clock className="w-4 h-4 text-brand-orange" /> Still there?</DialogTitle>
          <DialogDescription className="font-body text-sm text-ink-2">
            You've been inactive for a while. To keep your account secure we'll sign you out in <strong className="text-ink font-mono" data-testid="idle-warning-countdown">{mm}:{ss}</strong> unless you confirm you're still here.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={() => logout()} className="rounded-full font-body text-sm" data-testid="idle-warning-logout">Sign out now</Button>
          <Button onClick={stay} className="rounded-full bg-brand-orange hover:bg-highlight-orange font-body text-sm" data-testid="idle-warning-stay">I'm still here</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
